import React, { useEffect, useMemo, useRef, useState } from 'react';
import './App.css';
import TaskAttachments from './components/TaskAttachments';
import logo from './assets/logo.png';

function App() {
  const [token, setToken] = useState(localStorage.getItem('token') || '');
  const [user, setUser] = useState(() => {
    const saved = localStorage.getItem('user');
    return saved ? JSON.parse(saved) : null;
  });

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [comments, setComments] = useState({});
  const [newComments, setNewComments] = useState({});
  const [attachments, setAttachments] = useState({});
  const [error, setError] = useState('');

  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [newTaskDescription, setNewTaskDescription] = useState('');
  const [newTaskDueDate, setNewTaskDueDate] = useState('');
  const [newTaskAssignedTo, setNewTaskAssignedTo] = useState('');
  const [newTaskFiles, setNewTaskFiles] = useState([]);

  const [newUserName, setNewUserName] = useState('');
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [newUserRole, setNewUserRole] = useState('user');

  const [editingUserId, setEditingUserId] = useState(null);
  const [editUserName, setEditUserName] = useState('');
  const [editUserEmail, setEditUserEmail] = useState('');
  const [editUserPassword, setEditUserPassword] = useState('');
  const [editUserRole, setEditUserRole] = useState('user');

  const [selectedTask, setSelectedTask] = useState(null);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showSettingsPanel, setShowSettingsPanel] = useState(false);
  const [showUserCreateForm, setShowUserCreateForm] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);

  const userMenuRef = useRef(null);

  const API = '/api';
  const isAdmin = user?.role === 'admin';

  const authHeaders = (withJson = false) => {
    const currentToken = localStorage.getItem('token') || token;
    const headers = {};

    if (currentToken) {
      headers.Authorization = `Bearer ${currentToken}`;
    }

    if (withJson) {
      headers['Content-Type'] = 'application/json';
    }

    return headers;
  };

  const formatDateTime = (value) => {
    if (!value) return '—';

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';

    return date.toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDateOnly = (value) => {
    if (!value) return '—';

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';

    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const formatFileSize = (size) => {
    if (!size && size !== 0) return '';
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
    if (size < 1024 * 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(1)} MB`;
    return `${(size / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  };

  const getUserInitials = (nameOrMail) => {
    if (!nameOrMail) return 'U';

    const parts = String(nameOrMail).trim().split(' ').filter(Boolean);

    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }

    return String(nameOrMail).trim().charAt(0).toUpperCase();
  };

  const login = async () => {
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || 'Login fehlgeschlagen');
      }

      setToken(data.token);
      setUser(data.user);
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      setError('');
    } catch (err) {
      setError(err.message || 'Login fehlgeschlagen');
    }
  };

  const fetchTasks = async () => {
    try {
      const res = await fetch(`${API}/tasks`, {
        headers: authHeaders(),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || 'Fehler beim Laden der Aufgaben');
      }

      setTasks(data.tasks || []);
    } catch (err) {
      setError(err.message || 'Fehler beim Laden der Aufgaben');
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await fetch(`${API}/users`, {
        headers: authHeaders(),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || 'Fehler beim Laden der Benutzer');
      }

      setUsers(data.users || []);
    } catch (err) {
      setError(err.message || 'Fehler beim Laden der Benutzer');
    }
  };

  const fetchComments = async (taskId) => {
    try {
      const res = await fetch(`${API}/tasks/comments/${taskId}`, {
        headers: authHeaders(),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || 'Fehler beim Laden der Kommentare');
      }

      setComments((prev) => ({
        ...prev,
        [taskId]: data.comments || [],
      }));
    } catch {
      setComments((prev) => ({
        ...prev,
        [taskId]: [],
      }));
    }
  };

  const fetchAttachments = async (taskId) => {
    try {
      const res = await fetch(`${API}/tasks/${taskId}/attachments`, {
        headers: authHeaders(),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || 'Fehler beim Laden der Anhänge');
      }

      setAttachments((prev) => ({
        ...prev,
        [taskId]: data.attachments || [],
      }));
    } catch {
      setAttachments((prev) => ({
        ...prev,
        [taskId]: [],
      }));
    }
  };

  const isOverdue = (task) => {
    if (!task.due_date || task.status === 'erledigt') return false;

    const today = new Date();
    const dueDate = new Date(task.due_date);

    today.setHours(0, 0, 0, 0);
    dueDate.setHours(0, 0, 0, 0);

    return dueDate < today;
  };

  const isDueSoon = (task) => {
    if (!task.due_date || task.status === 'erledigt') return false;

    const today = new Date();
    const dueDate = new Date(task.due_date);

    today.setHours(0, 0, 0, 0);
    dueDate.setHours(0, 0, 0, 0);

    const diffMs = dueDate - today;
    const diffDays = diffMs / (1000 * 60 * 60 * 24);

    return diffDays >= 0 && diffDays <= 3;
  };

  const getTaskCardClass = (task) => {
    if (task.status === 'erledigt') return 'task-card task-done';
    if (isOverdue(task)) return 'task-card task-overdue';
    if (isDueSoon(task)) return 'task-card task-due-soon';
    return 'task-card task-open';
  };

  const getStatusBadge = (task) => {
    if (task.status === 'erledigt') {
      return <span className="status-badge done">Erledigt</span>;
    }
    if (isOverdue(task)) {
      return <span className="status-badge overdue">Überfällig</span>;
    }
    if (isDueSoon(task)) {
      return <span className="status-badge due-soon">Bald fällig</span>;
    }
    return <span className="status-badge open">Offen</span>;
  };

  const getTaskMetaItems = (task) => [
    {
      label: 'Von',
      value: task.created_by_name || 'Unbekannt',
    },
    {
      label: 'An',
      value: task.assigned_to_name || '—',
    },
    {
      label: 'Erstellt am',
      value: formatDateOnly(task.created_at),
    },
  ];

  const openTaskModal = async (task) => {
    setSelectedTask(task);
    setShowTaskModal(true);

    await Promise.all([fetchComments(task.id), fetchAttachments(task.id)]);
  };

  const closeTaskModal = () => {
    setSelectedTask(null);
    setShowTaskModal(false);
  };

  const addComment = async (taskId) => {
    const text = (newComments[taskId] || '').trim();

    if (!text) return;

    try {
      const res = await fetch(`${API}/tasks/comments/${taskId}`, {
        method: 'POST',
        headers: authHeaders(true),
        body: JSON.stringify({ text }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || 'Fehler beim Speichern des Kommentars');
      }

      setNewComments((prev) => ({
        ...prev,
        [taskId]: '',
      }));

      await fetchComments(taskId);
      setError('');
    } catch (err) {
      setError(err.message || 'Fehler beim Speichern des Kommentars');
    }
  };

  const uploadSingleFileForTask = async (taskId, file) => {
    const formData = new FormData();
    formData.append('attachment', file);

    const res = await fetch(`${API}/tasks/${taskId}/attachments`, {
      method: 'POST',
      headers: authHeaders(false),
      body: formData,
    });

    const data = await res.json().catch(() => ({}));

    if (!res.ok) {
      throw new Error(data.message || `Anhang "${file.name}" konnte nicht hochgeladen werden`);
    }
  };

  const uploadFilesForTask = async (taskId, files) => {
    if (!files || files.length === 0) return;

    for (const file of files) {
      await uploadSingleFileForTask(taskId, file);
    }
  };

  const handleNewTaskFilesChange = (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (selectedFiles.length === 0) return;

    setNewTaskFiles((prev) => [...prev, ...selectedFiles]);
    e.target.value = '';
  };

  const removeNewTaskFile = (indexToRemove) => {
    setNewTaskFiles((prev) => prev.filter((_, index) => index !== indexToRemove));
  };

  const createTask = async () => {
    if (!newTaskTitle.trim()) {
      setError('Bitte einen Titel eingeben');
      return;
    }

    try {
      const res = await fetch(`${API}/tasks`, {
        method: 'POST',
        headers: authHeaders(true),
        body: JSON.stringify({
          title: newTaskTitle.trim(),
          description: newTaskDescription.trim(),
          due_date: newTaskDueDate || null,
          assigned_to: newTaskAssignedTo || null,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || 'Fehler beim Erstellen der Aufgabe');
      }

      const createdTaskId = data.task?.id;

      if (createdTaskId && newTaskFiles.length > 0) {
        await uploadFilesForTask(createdTaskId, newTaskFiles);
      }

      setNewTaskTitle('');
      setNewTaskDescription('');
      setNewTaskDueDate('');
      setNewTaskAssignedTo('');
      setNewTaskFiles([]);
      setShowCreateForm(false);
      setError('');

      await fetchTasks();
    } catch (err) {
      setError(err.message || 'Fehler beim Erstellen der Aufgabe');
    }
  };

  const createUser = async () => {
    if (!newUserName.trim()) {
      setError('Bitte einen Namen eingeben');
      return;
    }

    if (!newUserEmail.trim()) {
      setError('Bitte eine E-Mail eingeben');
      return;
    }

    if (!newUserPassword.trim()) {
      setError('Bitte ein Passwort eingeben');
      return;
    }

    try {
      const res = await fetch(`${API}/users`, {
        method: 'POST',
        headers: authHeaders(true),
        body: JSON.stringify({
          name: newUserName.trim(),
          email: newUserEmail.trim(),
          password: newUserPassword,
          role: newUserRole,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || 'Fehler beim Anlegen des Benutzers');
      }

      setNewUserName('');
      setNewUserEmail('');
      setNewUserPassword('');
      setNewUserRole('user');
      setShowUserCreateForm(false);
      setShowSettingsPanel(true);
      setShowUserMenu(false);
      setError('');

      await fetchUsers();
    } catch (err) {
      setError(err.message || 'Fehler beim Anlegen des Benutzers');
    }
  };

  const startEditUser = (listedUser) => {
    setEditingUserId(listedUser.id);
    setEditUserName(listedUser.name || '');
    setEditUserEmail(listedUser.email || '');
    setEditUserPassword('');
    setEditUserRole(listedUser.role || 'user');
  };

  const cancelEditUser = () => {
    setEditingUserId(null);
    setEditUserName('');
    setEditUserEmail('');
    setEditUserPassword('');
    setEditUserRole('user');
  };

  const updateUser = async (userId) => {
    if (!editUserName.trim()) {
      setError('Bitte einen Namen eingeben');
      return;
    }

    if (!editUserEmail.trim()) {
      setError('Bitte eine E-Mail eingeben');
      return;
    }

    try {
      const body = {
        name: editUserName.trim(),
        email: editUserEmail.trim(),
        role: editUserRole,
      };

      if (editUserPassword.trim()) {
        body.password = editUserPassword.trim();
      }

      const res = await fetch(`${API}/users/${encodeURIComponent(userId)}`, {
        method: 'PUT',
        headers: authHeaders(true),
        body: JSON.stringify(body),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || 'Fehler beim Aktualisieren des Benutzers');
      }

      if (Number(user?.id) === Number(userId)) {
        const updatedCurrentUser = {
          ...user,
          name: data.user.name,
          email: data.user.email,
          role: data.user.role,
        };
        setUser(updatedCurrentUser);
        localStorage.setItem('user', JSON.stringify(updatedCurrentUser));
      }

      cancelEditUser();
      setError('');
      await fetchUsers();
    } catch (err) {
      setError(err.message || 'Fehler beim Aktualisieren des Benutzers');
    }
  };

  const deleteUser = async (userId, userName) => {
    if (userId === null || userId === undefined || userId === '') {
      setError('Benutzer-ID fehlt. Löschen nicht möglich.');
      return;
    }

    const confirmed = window.confirm(`Willst du den Benutzer "${userName}" wirklich löschen?`);

    if (!confirmed) return;

    try {
      const res = await fetch(`${API}/users/${encodeURIComponent(userId)}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });

      let data = {};
      const responseText = await res.text();

      if (responseText) {
        try {
          data = JSON.parse(responseText);
        } catch {
          data = {};
        }
      }

      if (!res.ok) {
        throw new Error(data.message || 'Fehler beim Löschen des Benutzers');
      }

      if (Number(editingUserId) === Number(userId)) {
        cancelEditUser();
      }

      setUsers((prev) => prev.filter((listedUser) => Number(listedUser.id) !== Number(userId)));
      setError('');
    } catch (err) {
      setError(err.message || 'Fehler beim Löschen des Benutzers');
    }
  };

  const deleteTask = async (id) => {
    const confirmed = window.confirm('Willst du diese Aufgabe wirklich löschen?');

    if (!confirmed) return;

    try {
      const res = await fetch(`${API}/tasks/${id}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || 'Fehler beim Löschen der Aufgabe');
      }

      setTasks((prev) => prev.filter((task) => task.id !== id));

      if (selectedTask && selectedTask.id === id) {
        closeTaskModal();
      }

      setError('');
    } catch (err) {
      setError(err.message || 'Fehler beim Löschen der Aufgabe');
    }
  };

  const updateStatus = async (id, status) => {
    try {
      const res = await fetch(`${API}/tasks/${id}`, {
        method: 'PUT',
        headers: authHeaders(true),
        body: JSON.stringify({ status }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || 'Fehler beim Ändern des Status');
      }

      setTasks((prev) =>
        prev.map((task) =>
          task.id === id
            ? {
                ...task,
                ...data.task,
                status: data.task?.status || status,
              }
            : task
        )
      );

      if (selectedTask && selectedTask.id === id) {
        setSelectedTask((prev) =>
          prev
            ? {
                ...prev,
                ...data.task,
                status: data.task?.status || status,
              }
            : prev
        );
      }

      setError('');
    } catch (err) {
      setError(err.message || 'Fehler beim Ändern des Status');
    }
  };

  const canDeleteTask = (task) => {
    if (isAdmin) return true;

    const creatorId = task.created_by ?? task.created_by_id ?? task.user_id ?? null;

    if (creatorId !== null && creatorId !== undefined) {
      return Number(creatorId) === Number(user?.id);
    }

    if (task.created_by_name && user?.name) {
      return task.created_by_name === user.name;
    }

    return false;
  };

  const toggleSettingsPanel = () => {
    setShowSettingsPanel((prev) => !prev);
    setShowUserCreateForm(false);
    cancelEditUser();
    setShowUserMenu(false);
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setToken('');
    setUser(null);
    setSelectedTask(null);
    setShowTaskModal(false);
    setShowCreateForm(false);
    setShowSettingsPanel(false);
    setShowUserCreateForm(false);
    setShowUserMenu(false);
    cancelEditUser();
  };

  useEffect(() => {
    if (token) {
      fetchTasks();
      fetchUsers();
    }
  }, [token]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const myOpenTasks = useMemo(
    () =>
      tasks.filter(
        (task) => task.status !== 'erledigt' && Number(task.assigned_to) === Number(user?.id)
      ),
    [tasks, user]
  );

  const otherOpenTasks = useMemo(
    () =>
      tasks.filter(
        (task) => task.status !== 'erledigt' && Number(task.assigned_to) !== Number(user?.id)
      ),
    [tasks, user]
  );

  const completedTasks = useMemo(
    () => tasks.filter((task) => task.status === 'erledigt'),
    [tasks]
  );

  if (!token) {
    return (
      <div className="auth-shell">
        <div className="auth-card">
          <div className="auth-logo-wrap">
            <img src={logo} alt="FFSW Logo" className="auth-logo" />
          </div>
          <div className="auth-badge">FFSW</div>
          <h1 className="auth-title">Aufgaben-App</h1>
          <p className="auth-subtitle">Bitte anmelden, um Aufgaben zu verwalten.</p>

          <div className="auth-form">
            <input
              className="app-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="E-Mail"
            />

            <input
              className="app-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Passwort"
            />

            <button type="button" className="primary-btn auth-login-btn" onClick={login}>
              Login
            </button>
          </div>

          {error && <div className="error-banner">{error}</div>}
        </div>
      </div>
    );
  }

  const renderMetaLine = (task, className = 'task-meta-line') => {
    const items = getTaskMetaItems(task);

    return (
      <div className={className}>
        {items.map((item, index) => (
          <React.Fragment key={`${item.label}-${item.value}-${index}`}>
            <span className="task-meta-inline-item">
              <span className="task-meta-inline-label">{item.label}</span>
              <span className="task-meta-inline-value">{item.value}</span>
            </span>

            {index < items.length - 1 && <span className="task-meta-separator">•</span>}
          </React.Fragment>
        ))}
      </div>
    );
  };

  const renderTaskCard = (task) => (
    <div
      key={task.id}
      className={getTaskCardClass(task)}
      onClick={() => openTaskModal(task)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          openTaskModal(task);
        }
      }}
    >
      <div className="task-card-top">
        <div className="task-card-title-group">
          <h3 className="task-card-title">{task.title}</h3>
        </div>

        <div className="task-card-top-right">
          <div className="task-card-date-inline">
            <span className="task-card-date-label">Fällig</span>
            <span className="task-card-date-value">{formatDateOnly(task.due_date)}</span>
          </div>
          <div className="task-card-badge-wrap">{getStatusBadge(task)}</div>
        </div>
      </div>

      {renderMetaLine(task)}

      <div className="task-card-footer" onClick={(e) => e.stopPropagation()}>
        <div className="task-card-actions">
          <button
            type="button"
            className="soft-btn detail-btn"
            onClick={() => openTaskModal(task)}
          >
            Details
          </button>

          <div className="status-select-wrap compact-status-wrap">
            <label className="status-select-label">Status</label>
            <select
              className="status-select prominent-status-select"
              value={task.status || 'offen'}
              onChange={(e) => updateStatus(task.id, e.target.value)}
            >
              <option value="offen">Offen</option>
              <option value="erledigt">Erledigt</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="app-shell">
      <div className="app-container">
        <header className="topbar">
          <div className="topbar-inner">
            <div className="topbar-left">
              <div className="topbar-logo">
                <img src={logo} alt="FFSW Logo" className="topbar-logo-image" />
              </div>

              <div className="topbar-brand">
                <div className="topbar-kicker">FFSW Aufgaben-App</div>
                <h1 className="topbar-title">Aufgabenmanagement</h1>
                <p className="topbar-subtitle">
                  Aufgaben organisieren, zuweisen und gemeinsam erledigen
                </p>
              </div>
            </div>

            <div className="topbar-right">
              <button
                type="button"
                className={`create-toggle-btn ${showCreateForm ? 'active' : ''}`}
                onClick={() => {
                  setShowCreateForm((prev) => !prev);
                  setShowSettingsPanel(false);
                  setShowUserCreateForm(false);
                  cancelEditUser();
                  setShowUserMenu(false);
                }}
              >
                <span className="create-toggle-icon">{showCreateForm ? '–' : '+'}</span>
                <span>Aufgabe erstellen</span>
              </button>

              <div ref={userMenuRef} className="profile-menu">
                <button
                  type="button"
                  className="profile-menu-trigger"
                  onClick={() => setShowUserMenu((prev) => !prev)}
                >
                  <span className="profile-menu-avatar">
                    {getUserInitials(user?.name || user?.email)}
                  </span>

                  <span className="profile-menu-user">
                    <span className="profile-menu-name">
                      {user?.name || user?.email || 'Benutzer'}
                    </span>
                    <span className="profile-menu-role">
                      {isAdmin ? 'Administrator' : 'Benutzer'}
                    </span>
                  </span>

                  <span className="profile-menu-chevron">{showUserMenu ? '▲' : '▼'}</span>
                </button>

                {showUserMenu && (
                  <div className="profile-menu-dropdown">
                    <div className="profile-menu-dropdown-header">
                      <p className="profile-menu-dropdown-name">{user?.name || 'Benutzer'}</p>
                      <p className="profile-menu-dropdown-mail">{user?.email || 'Keine E-Mail'}</p>
                    </div>

                    <div className="profile-menu-list">
                      {isAdmin && (
                        <button
                          type="button"
                          className="profile-menu-item"
                          onClick={toggleSettingsPanel}
                        >
                          <span className="profile-menu-item-label">
                            <span className="profile-menu-item-title">Einstellungen</span>
                            <span className="profile-menu-item-subtitle">
                              Benutzer und Rollen verwalten
                            </span>
                          </span>
                          <span className="profile-menu-item-icon">
                            {showSettingsPanel ? '•' : '›'}
                          </span>
                        </button>
                      )}

                      <button
                        type="button"
                        className="profile-menu-item profile-menu-item-danger"
                        onClick={logout}
                      >
                        <span className="profile-menu-item-label">
                          <span className="profile-menu-item-title">Logout</span>
                          <span className="profile-menu-item-subtitle">Sitzung beenden</span>
                        </span>
                        <span className="profile-menu-item-icon">↗</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        {isAdmin && showSettingsPanel && (
          <section className="create-task-card compact-create-card">
            <div className="create-task-header compact-create-header">
              <div>
                <h2>Einstellungen</h2>
                <p>Benutzer verwalten und neue Benutzer anlegen.</p>
              </div>
            </div>

            <div className="create-form-actions user-settings-actions">
              <button
                type="button"
                className="primary-btn"
                onClick={() => {
                  setShowUserCreateForm((prev) => !prev);
                  cancelEditUser();
                }}
              >
                {showUserCreateForm ? 'Benutzerformular schließen' : 'Neuen Benutzer anlegen'}
              </button>
            </div>

            {showUserCreateForm && (
              <div className="create-task-grid user-create-grid">
                <div className="create-field">
                  <label>Name</label>
                  <input
                    className="app-input"
                    value={newUserName}
                    onChange={(e) => setNewUserName(e.target.value)}
                    placeholder="Vor- und Nachname"
                  />
                </div>

                <div className="create-field">
                  <label>E-Mail</label>
                  <input
                    className="app-input"
                    value={newUserEmail}
                    onChange={(e) => setNewUserEmail(e.target.value)}
                    placeholder="name@beispiel.de"
                  />
                </div>

                <div className="create-field">
                  <label>Passwort</label>
                  <input
                    className="app-input"
                    type="password"
                    value={newUserPassword}
                    onChange={(e) => setNewUserPassword(e.target.value)}
                    placeholder="Mindestens 6 Zeichen"
                  />
                </div>

                <div className="create-field">
                  <label>Rolle</label>
                  <select
                    className="app-input app-select large-select"
                    value={newUserRole}
                    onChange={(e) => setNewUserRole(e.target.value)}
                  >
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>

                <div className="create-field field-span-2">
                  <div className="create-form-actions">
                    <button
                      type="button"
                      className="soft-btn"
                      onClick={() => {
                        setShowUserCreateForm(false);
                        setNewUserName('');
                        setNewUserEmail('');
                        setNewUserPassword('');
                        setNewUserRole('user');
                      }}
                    >
                      Abbrechen
                    </button>

                    <button type="button" className="primary-btn" onClick={createUser}>
                      Benutzer anlegen
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div>
              <h3 className="settings-section-title">Benutzer</h3>

              {users.length === 0 ? (
                <div className="empty-state">Keine Benutzer vorhanden.</div>
              ) : (
                <div className="task-list">
                  {users.map((listedUser) => {
                    const isEditing = Number(editingUserId) === Number(listedUser.id);

                    return (
                      <div key={listedUser.id} className="task-card task-open user-card">
                        <div className="task-card-top">
                          <div className="task-card-title-group">
                            <h3 className="task-card-title">{listedUser.name}</h3>
                          </div>

                          <div className="task-card-top-right">
                            <div className="task-card-badge-wrap">
                              <span
                                className={`status-badge ${
                                  listedUser.role === 'admin' ? 'overdue' : 'open'
                                }`}
                              >
                                {listedUser.role === 'admin' ? 'Admin' : 'User'}
                              </span>
                            </div>
                          </div>
                        </div>

                        {!isEditing ? (
                          <div className="task-card-footer user-card-footer">
                            <div className="task-card-actions">
                              <button
                                type="button"
                                className="soft-btn detail-btn"
                                onClick={() => {
                                  setShowUserCreateForm(false);
                                  startEditUser(listedUser);
                                }}
                              >
                                Bearbeiten
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="user-edit-panel">
                            <div className="create-task-grid">
                              <div className="create-field">
                                <label>Name</label>
                                <input
                                  className="app-input"
                                  value={editUserName}
                                  onChange={(e) => setEditUserName(e.target.value)}
                                />
                              </div>

                              <div className="create-field">
                                <label>E-Mail</label>
                                <input
                                  className="app-input"
                                  value={editUserEmail}
                                  onChange={(e) => setEditUserEmail(e.target.value)}
                                />
                              </div>

                              <div className="create-field">
                                <label>Neues Passwort</label>
                                <input
                                  className="app-input"
                                  type="password"
                                  value={editUserPassword}
                                  onChange={(e) => setEditUserPassword(e.target.value)}
                                  placeholder="Leer lassen, wenn unverändert"
                                />
                              </div>

                              <div className="create-field">
                                <label>Rolle</label>
                                <select
                                  className="app-input app-select large-select"
                                  value={editUserRole}
                                  onChange={(e) => setEditUserRole(e.target.value)}
                                >
                                  <option value="user">User</option>
                                  <option value="admin">Admin</option>
                                </select>
                              </div>
                            </div>

                            <div className="create-form-actions">
                              <button type="button" className="soft-btn" onClick={cancelEditUser}>
                                Abbrechen
                              </button>

                              <button
                                type="button"
                                className="primary-btn"
                                onClick={() => updateUser(listedUser.id)}
                              >
                                Speichern
                              </button>

                              <button
                                type="button"
                                className="ghost-danger-btn"
                                onClick={() => deleteUser(listedUser.id, listedUser.name)}
                              >
                                Benutzer löschen
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </section>
        )}

        {showCreateForm && (
          <section className="create-task-card compact-create-card">
            <div className="create-task-header compact-create-header">
              <div>
                <h2>Neue Aufgabe</h2>
                <p>Titel, Beschreibung, Frist, Zuweisung und Anhänge eingeben.</p>
              </div>
            </div>

            <div className="create-task-grid">
              <div className="create-field field-span-2">
                <label>Titel</label>
                <input
                  className="app-input"
                  value={newTaskTitle}
                  onChange={(e) => setNewTaskTitle(e.target.value)}
                  placeholder="z. B. Protokoll Vorstandssitzung fertigstellen"
                />
              </div>

              <div className="create-field field-span-2">
                <label>Beschreibung</label>
                <textarea
                  className="app-textarea"
                  value={newTaskDescription}
                  onChange={(e) => setNewTaskDescription(e.target.value)}
                  placeholder="Weitere Details zur Aufgabe..."
                />
              </div>

              <div className="create-field create-date-field">
                <label>Fälligkeitsdatum</label>
                <input
                  className="app-input"
                  type="date"
                  value={newTaskDueDate}
                  onChange={(e) => setNewTaskDueDate(e.target.value)}
                />
              </div>

              <div className="create-field create-assign-field">
                <label>Zuweisen an</label>
                <select
                  className="app-input app-select large-select"
                  value={newTaskAssignedTo}
                  onChange={(e) => setNewTaskAssignedTo(e.target.value)}
                >
                  <option value="">Bitte auswählen</option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="create-field field-span-2">
                <label>Anhänge</label>
                <input
                  className="app-input"
                  type="file"
                  multiple
                  onChange={handleNewTaskFilesChange}
                />

                {newTaskFiles.length > 0 && (
                  <div className="selected-files-list">
                    {newTaskFiles.map((file, index) => (
                      <div
                        key={`${file.name}-${file.lastModified}-${index}`}
                        className="selected-file-item"
                      >
                        <div className="selected-file-info">
                          <div className="selected-file-name">{file.name}</div>
                          <div className="selected-file-size">{formatFileSize(file.size)}</div>
                        </div>

                        <button
                          type="button"
                          className="soft-btn"
                          onClick={() => removeNewTaskFile(index)}
                        >
                          Entfernen
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="create-form-actions">
              <button
                type="button"
                className="soft-btn"
                onClick={() => {
                  setShowCreateForm(false);
                  setNewTaskFiles([]);
                }}
              >
                Abbrechen
              </button>

              <button type="button" className="primary-btn" onClick={createTask}>
                Speichern
              </button>
            </div>
          </section>
        )}

        {error && <div className="error-banner page-error">{error}</div>}

        <section className="task-section">
          <div className="section-header">
            <div>
              <div className="section-kicker">Aktuell bei dir</div>
              <h2 className="section-title">Meine offenen Aufgaben</h2>
              <p className="section-subtitle">Aufgaben, die aktuell dir zugewiesen sind.</p>
            </div>
            <span className="section-count">{myOpenTasks.length}</span>
          </div>

          {myOpenTasks.length === 0 ? (
            <div className="empty-state">Keine offenen Aufgaben vorhanden.</div>
          ) : (
            <div className="task-list">{myOpenTasks.map((task) => renderTaskCard(task))}</div>
          )}
        </section>

        <section className="task-section">
          <div className="section-header">
            <div>
              <div className="section-kicker">Weitere Aufgaben</div>
              <h2 className="section-title">Weitere offene Aufgaben</h2>
              <p className="section-subtitle">Offene Aufgaben anderer Mitglieder.</p>
            </div>
            <span className="section-count">{otherOpenTasks.length}</span>
          </div>

          {otherOpenTasks.length === 0 ? (
            <div className="empty-state">Keine weiteren offenen Aufgaben vorhanden.</div>
          ) : (
            <div className="task-list">{otherOpenTasks.map((task) => renderTaskCard(task))}</div>
          )}
        </section>

        <section className="task-section">
          <div className="section-header">
            <div>
              <div className="section-kicker">Abgeschlossen</div>
              <h2 className="section-title">Erledigte Aufgaben</h2>
              <p className="section-subtitle">Bereits abgeschlossene Vorgänge.</p>
            </div>
            <span className="section-count">{completedTasks.length}</span>
          </div>

          {completedTasks.length === 0 ? (
            <div className="empty-state">Keine erledigten Aufgaben vorhanden.</div>
          ) : (
            <div className="task-list">{completedTasks.map((task) => renderTaskCard(task))}</div>
          )}
        </section>

        {showTaskModal && selectedTask && (
          <div className="modal-overlay" onClick={closeTaskModal}>
            <div className="task-modal" onClick={(e) => e.stopPropagation()}>
              <div className="task-modal-header">
                <div className="task-modal-title-wrap">
                  <div className="task-modal-kicker">Aufgabendetails</div>
                  <h2>{selectedTask.title}</h2>
                </div>

                <button type="button" className="soft-btn" onClick={closeTaskModal}>
                  Schließen
                </button>
              </div>

              <div className="task-modal-status-panel">
                <div className="task-modal-top-info">
                  <div className="task-modal-top-right">
                    <div className="task-card-date-inline modal-date-inline">
                      <span className="task-card-date-label">Fällig</span>
                      <span className="task-card-date-value">
                        {formatDateOnly(selectedTask.due_date)}
                      </span>
                    </div>

                    <div className="modal-status-badge-wrap">{getStatusBadge(selectedTask)}</div>
                  </div>

                  {renderMetaLine(selectedTask, 'modal-meta-line')}
                </div>

                <div className="status-select-wrap modal-status-select-wrap">
                  <label className="status-select-label">Status ändern</label>
                  <select
                    className="status-select modal-prominent-status-select"
                    value={selectedTask.status || 'offen'}
                    onChange={(e) => updateStatus(selectedTask.id, e.target.value)}
                  >
                    <option value="offen">Offen</option>
                    <option value="erledigt">Erledigt</option>
                  </select>
                </div>
              </div>

              <div className="modal-detail-grid">
                <div className="modal-detail-card modal-detail-card-full">
                  <span className="modal-detail-label">Beschreibung</span>
                  <div className="modal-detail-value modal-description">
                    {selectedTask.description || 'Keine Beschreibung vorhanden'}
                  </div>
                </div>
              </div>

              <TaskAttachments
                taskId={selectedTask.id}
                token={token}
                attachments={attachments[selectedTask.id] || []}
                onRefresh={() => fetchAttachments(selectedTask.id)}
              />

              {canDeleteTask(selectedTask) && (
                <div className="modal-danger-zone">
                  <button
                    type="button"
                    className="ghost-danger-btn modal-delete-btn"
                    onClick={() => deleteTask(selectedTask.id)}
                  >
                    Aufgabe löschen
                  </button>
                </div>
              )}

              <div className="modal-comments">
                <div className="modal-comments-header">
                  <h3>Kommentare</h3>
                  <span className="comment-count">
                    {(comments[selectedTask.id] || []).length}
                  </span>
                </div>

                {(comments[selectedTask.id] || []).length === 0 && (
                  <div className="empty-comments">Noch keine Kommentare vorhanden.</div>
                )}

                <div className="comment-list">
                  {(comments[selectedTask.id] || []).map((c) => (
                    <div key={c.id} className="comment-item">
                      <div className="comment-avatar">
                        {(c.user_name || '?').charAt(0).toUpperCase()}
                      </div>

                      <div className="comment-body">
                        <div className="comment-topline">
                          <span className="comment-author">{c.user_name || 'Unbekannt'}</span>
                          <span className="comment-date">{formatDateTime(c.created_at)}</span>
                        </div>
                        <div className="comment-text">{c.text}</div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="comment-form">
                  <label>Neuer Kommentar</label>
                  <textarea
                    className="app-textarea comment-textarea"
                    value={newComments[selectedTask.id] || ''}
                    onChange={(e) =>
                      setNewComments((prev) => ({
                        ...prev,
                        [selectedTask.id]: e.target.value,
                      }))
                    }
                    placeholder="Kommentar schreiben..."
                  />

                  <div className="comment-form-actions">
                    <button
                      type="button"
                      className="primary-btn"
                      onClick={() => addComment(selectedTask.id)}
                    >
                      Kommentar speichern
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default App;